/**
 * The phone's calendar — `docs/activity.md` §10.2, rebuilt on Aaron's Oct 25
 * phone notes 3, 5, 6 and 7 after Outlook's phone calendar.
 *
 * Four views, picked by the view button beside the people button in the
 * page's header (the header is the page's) and remembered per device
 * (`state.calendar.phoneView`):
 *
 * - **Day** — the default (note 7): the week strip (`PhoneStrip` — its
 *   month button opens the month), and the day's hours (`PhoneDay`), edge to
 *   edge; the day line is the page bar's sub (Aaron, Oct 27, note 1.1.1 —
 *   `phone-sub.ts`, said through `useActivitySub`);
 * - **3 days** — the same strip spread over three columns of hours, the sub
 *   the three days and their total; a day tapped in the strip becomes the
 *   first of the three, and only the view button changes the view (answer 5);
 * - **Month** — month after month, every entry a coloured line (`PhoneMonth`);
 * - **List** — the agenda, endless both ways, the strip above following the
 *   day at its top (`PhoneAgenda`).
 *
 * A touch held on an empty slot of Day or 3 days makes an hour there, which
 * edit mode lengthens or shortens and **Next** opens New meeting for as a
 * sheet (note 5; Aaron, Oct 27, answer 6); a touch held on something there
 * lifts it to move it, Outlook's way (note 1.1.4 — `phone-lift.tsx`). Every
 * minute, mile and colour is the server's (rule 2).
 */
import { useMemo, useState } from 'react'
import { addDays } from '@rowted/shared'
import { openingMinutes } from './drag.js'
import { useActivity } from '../activity-context.js'
import { activityRequest } from '../api.js'
import { CalendarOverlays } from './overlays.js'
import { PhoneDay, stripInfo, threeDayInset, type PhoneSlot } from './phone-day.js'
import { phoneDaySub, phoneDaysWords } from './phone-sub.js'
import { useActivitySub } from '../page-sub.js'
import { PhoneStrip } from './phone-strip.js'
import { phoneDays, phoneOpening, phoneRange } from './phone-time.js'
import { PhoneMonth } from './phone-month.js'
import { PhoneAgenda } from './phone-agenda.js'
import { useCalendar } from './use-calendar.js'
import { useNowMinutes } from './now.js'
import { usePhoneLift } from './phone-lift.js'

/* Opening a stop from the phone — moved beside Month and List, which open them too. */
export { ACTIVITY_STOP_EVENT, openStop } from './open-item.js'

/** Somewhere for New meeting to stand: on a phone it is a sheet, so it never asks. */
const NOWHERE = { getBoundingClientRect: () => new DOMRect(0, 0, 1, 1) }

export function PhoneCalendar() {
  const { state, change, people, today, me, directory } = useActivity()
  const view = state.calendar.phoneView
  const date = state.calendar.date
  const show = state.calendar.show
  const several = people.length > 1
  const days = useMemo(() => phoneDays(view, date), [view, date])
  const range = phoneRange(view === 'three' ? 'three' : 'day', date)
  /* Month reads its own months; Day, 3 days and the strip over List read the strip's week. */
  const { answer, error, reload } = useCalendar(view === 'month' ? [] : people, range.from, range.to, false)
  const now = useNowMinutes(answer)
  const [scrollTo, setScrollTo] = useState<{ minutes: number; nonce: number } | null>(null)
  const [slot, setSlot] = useState<PhoneSlot | null>(null)
  /*
   * The line under *Activity* (Aaron, Oct 27, note 1.1.1): on Day and 3 days the day line, the
   * days themselves while the answer is on its way so the title never moves; Month and List
   * have their own headings and say nothing there.
   */
  const timed = view === 'day' || view === 'three'
  const sub = !timed ? null : answer && people.length > 0 ? phoneDaySub(answer, days) : phoneDaysWords(days)
  useActivitySub(sub)
  /* Held and dragged, Outlook's way (Aaron, Oct 27, note 1.1.4), written as the desktop writes a drag. */
  const lifting = usePhoneLift({
    answerKey: answer, where: `${view}|${days.join(',')}`, zone: answer?.timeZone ?? me.user.timeZone,
    canSend: directory ? directory.people.some((p) => p.id === me.user.id && p.inbox) : true,
    reload, onNext: setSlot,
    titleOf: (personId) => {
      const first = personId === me.user.id ? null : answer?.people.find((p) => p.id === personId)?.firstName
      return first ? `New meeting for ${first}` : 'New meeting'
    },
  })

  const setDate = (next: string) => change({ calendar: { date: next } })
  const openDay = (day: string) => change({ calendar: { date: day, phoneView: 'day' } })
  const canBook = (personId: string) => directory?.people.find((p) => p.id === personId)?.book ?? true

  if (view === 'month') {
    return (
      <div className="flex min-h-0 flex-1 flex-col [line-height:normal]">
        <PhoneMonth date={date} people={people} onPickDay={openDay} />
      </div>
    )
  }

  const strip = (
    <PhoneStrip view={view} date={date} today={today} people={people} show={show}
      dayOf={(day) => stripInfo(answer, day, show)}
      inset={view === 'three' ? threeDayInset : undefined}
      onDate={setDate}
      onToday={() => { setDate(today); setScrollTo({ minutes: now, nonce: Date.now() }) }} />
  )

  if (view === 'list') {
    return (
      <div className="flex min-h-0 flex-1 flex-col [line-height:normal]">
        {strip}
        <div className="flex min-h-0 flex-1 flex-col">
          <PhoneAgenda date={date} people={people} onPickDay={setDate} />
        </div>
      </div>
    )
  }

  let body
  if (people.length === 0) {
    body = <div className="flex-1 bg-bg p-4 text-[13px] text-text-muted">Unassigned has no calendar.</div>
  } else if (!answer) {
    body = <div className="flex-1 bg-bg p-4 text-[13px] text-text-muted">{error ?? 'Loading…'}</div>
  } else {
    /* The desktop's opening rule over the days shown — and on today, late in the day, now in view. */
    const shown = { people: answer.people.map((person) => ({ ...person, days: person.days.filter((d) => days.includes(d.date)) })) }
    const startAt = phoneOpening(openingMinutes(shown, state.calendar.startHour), now, days.includes(today))
    body = (
      <PhoneDay answer={answer} days={days} today={today} now={now} several={several} byPerson={view === 'day' && several}
        show={show} startAt={startAt} scrollTo={scrollTo} slot={slot} canBook={canBook} meId={me.user.id}
        lift={lifting.lift} liftDrives={lifting.drives} onLift={lifting.setLift} onDrop={lifting.drop}
        onStep={(direction) => setDate(addDays(date, direction * days.length))}
        onTick={(item) => {
          void activityRequest(`/activities/${item.id}/done`, { method: 'POST', body: { done: !item.done } }).then(reload, reload)
        }} />
    )
  }

  return (
    /* The page ends at the top of the tab bar; the day fills what is left and scrolls in itself (Aaron, Oct 23). */
    <div className="flex min-h-0 flex-1 flex-col [line-height:normal]">
      {strip}
      <div className="flex min-h-0 flex-1 flex-col bg-bg">{body}</div>
      <CalendarOverlays
        action={slot ? {
          kind: 'slot', personId: slot.personId, date: slot.date, minutes: slot.start, anchor: NOWHERE, length: slot.minutes,
          personName: answer?.people.find((p) => p.id === slot.personId)?.name ?? null,
        } : null}
        onClose={() => setSlot(null)} onChanged={reload} me={me.user.id}
        timeZone={answer?.timeZone ?? me.user.timeZone} slotPlacement="sheet" />
      {lifting.overlays}
    </div>
  )
}
